import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { LuImagePlus } from "react-icons/lu";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoEyeOutline } from "react-icons/io5";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { safeSetLocalStorage, safeGetParsedLocalStorage } from "../utils/localStorage";

function SellImages({ imagesArr = [], setImagesArr, labels = [], setlabels, setImagesForApi, imagesForApi }) {
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Zoom state
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 4;
    const ZOOM_SPEED = 0.1;

    // Load from localStorage
    useEffect(() => {
        const parsedImages = safeGetParsedLocalStorage("sellImages", []);
        if (parsedImages && parsedImages.length > 0) {
            setImagesArr(parsedImages);
        }
    }, []);

    // Reset zoom when image changes or modal opens
    const resetZoom = useCallback(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, []);

    // Handle opening the modal
    const openModal = (index) => {
        setSelectedImageIndex(index);
        setIsModalOpen(true);
        resetZoom();
        document.body.style.overflow = "hidden";
    };

    // Handle closing the modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImageIndex(null);
        resetZoom();
        document.body.style.overflow = "unset";
    };

    // Navigate to previous image
    const goToPrevImage = useCallback(() => {
        if (selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
            resetZoom();
        }
    }, [selectedImageIndex, resetZoom]);

    // Navigate to next image
    const goToNextImage = useCallback(() => {
        if (selectedImageIndex < imagesArr.length - 1) {
            setSelectedImageIndex(selectedImageIndex + 1);
            resetZoom();
        }
    }, [selectedImageIndex, imagesArr.length, resetZoom]);

    // Handle keyboard events for navigation and zoom
    useEffect(() => {
        if (!isModalOpen) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case "Escape":
                    closeModal();
                    break;
                case "ArrowLeft":
                    e.preventDefault();
                    goToPrevImage();
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    goToNextImage();
                    break;
                case "+":
                case "=":
                    e.preventDefault();
                    setScale((prev) => Math.min(MAX_SCALE, prev + ZOOM_SPEED));
                    break;
                case "-":
                case "_":
                    e.preventDefault();
                    setScale((prev) => Math.max(MIN_SCALE, prev - ZOOM_SPEED));
                    break;
                case "0":
                    e.preventDefault();
                    resetZoom();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isModalOpen, goToPrevImage, goToNextImage, resetZoom]);

    // Handle mouse wheel zoom
    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY * -0.01;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta * ZOOM_SPEED * scale));
        setScale(newScale);
    };

    // Handle zoom in
    const zoomIn = () => {
        setScale((prev) => Math.min(MAX_SCALE, prev + ZOOM_SPEED * 2));
    };

    // Handle zoom out
    const zoomOut = () => {
        setScale((prev) => Math.max(MIN_SCALE, prev - ZOOM_SPEED * 2));
    };

    // Handle mouse drag to pan when zoomed
    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            const dx = (e.clientX - dragStart.x) / scale;
            const dy = (e.clientY - dragStart.y) / scale;
            setPosition({
                x: position.x - dx,
                y: position.y - dy,
            });
            setDragStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Convert File to Base64
    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    const handleDrop = async (acceptedFiles) => {
        const acceptedTypes = ["image/png", "image/jpeg", "image/jpg"];

        const validFiles = acceptedFiles.filter((file) => acceptedTypes.includes(file.type));

        const imageFiles = validFiles.filter((file) => file.type.startsWith("image/"));
        const nonImageFiles = validFiles.filter((file) => !file.type.startsWith("image/"));

        if (nonImageFiles.length > 0) {
            setErrorMessage("Only image files are allowed");
            return;
        }

        const imageData = await Promise.all(
            imageFiles.map(async (file) => {
                const base64 = await toBase64(file);
                return {
                    preview: URL.createObjectURL(file),
                    path: file.name,
                    base64, // store for persistence
                };
            })
        );

        // Update state and localStorage
        const updatedImages = [...imagesArr, ...imageData];
        setImagesArr(updatedImages);

        // Try to save to localStorage with error handling
        const success = safeSetLocalStorage("sellImages", JSON.stringify(updatedImages), setErrorMessage);
        if (!success) {
            // If storage fails, still keep the images in memory but warn user
            console.warn("Images loaded but not saved to local storage");
        }

        // Keep files for API submission
        setImagesForApi((prev) => [...prev, ...imageFiles]);
        setlabels((prev) => [...prev, ...imageFiles.map(() => "")]);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
        },

        multiple: true,
    });

    const handleDeleteImage = (index) => {
        const updatedImages = imagesArr.filter((_, i) => i !== index);
        setImagesArr(updatedImages);

        // Try to save to localStorage with error handling
        safeSetLocalStorage("sellImages", JSON.stringify(updatedImages), setErrorMessage);

        setlabels((prev) => prev.filter((_, i) => i !== index));
        setImagesForApi((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="tw-flex tw-justify-center tw-w-full">
            <div className="tw-w-[80%] max-sm:tw-w-[100%]">
                <div className="tw-text-center tw-flex tw-flex-col tw-gap-[10px]">
                    <p className="tw-font-[800] tw-text-[26px] tw-text-black">Upload Images Of Your Property</p>
                    <p className="tw-font-sans tw-font-normal tw-text-sm tw-leading-[150%] tw-text-black">
                        Please upload clear, high-quality images of your property. Make sure the photos are well-lit and capture key areas such as the
                        interior rooms, exterior views, and any unique features. <br />
                        <span className="tw-text-red-500">Please make sure the images are in JPEG, PNG, or JPG format.</span>
                    </p>
                </div>

                {errorMessage && <p className="tw-text-red-500 tw-text-[14px] tw-mt-2">{errorMessage}</p>}

                <div className="tw-flex tw-gap-2 tw-w-full max-sm:tw-flex-wrap">
                    <div className={`${imagesArr.length > 0 ? "tw-w-[40%] max-sm:tw-w-full" : "tw-w-full"}`}>
                        <Box
                            {...getRootProps()}
                            className="uploadButton tw-border tw-w-full tw-rounded-lg tw-h-[250px] tw-mt-3 tw-border-dashed tw-border-primary tw-bg-[#FFF9F7] tw-min-h-[130px] tw-flex tw-justify-center tw-items-center tw-width"
                            style={{ cursor: "pointer" }}
                        >
                            <label className="tw-flex tw-flex-col tw-text-gray-400 tw-items-center tw-gap-3 tw-mb-2">
                                <LuImagePlus className="tw-text-[2.3rem] tw-text-primary" />
                                <span>Drop your files here</span>
                                <button className="tw-bg-primary hover:tw-bg-primary_hover tw-rounded-md tw-p-2 tw-text-white">Choose images</button>
                            </label>
                        </Box>
                        <input {...getInputProps()} accept="image/*" multiple />
                    </div>

                    {imagesArr.length > 0 && (
                        <div className="tw-w-[60%] max-sm:tw-w-full tw-h-[250px] tw-overflow-y-auto tw-mb-3">
                            {imagesArr.map((img, index) => (
                                <div
                                    onClick={() => openModal(index)}
                                    key={index}
                                    className="tw-flex tw-mt-3 tw-items-center tw-justify-between tw-border tw-rounded-md tw-p-1 tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors"
                                >
                                    <div className="tw-flex tw-items-center tw-gap-5">
                                        <img src={img?.base64 || img?.preview} className="tw-h-[50px] tw-w-[50px] tw-rounded-md tw-object-contain" />
                                        <p className="tw-text-primary">{img?.path}</p>
                                    </div>
                                    <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mr-2">
                                        <IoEyeOutline className="tw-h-[20px] tw-w-[20px] tw-text-primary tw-cursor-pointer" />
                                        <RiDeleteBin6Line
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteImage(index);
                                            }}
                                            className="tw-h-[20px] tw-w-[20px] tw-text-primary tw-cursor-pointer"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Enhanced Image Modal with Keyboard Navigation and Zoom */}
            {isModalOpen && selectedImageIndex !== null && (
                <div
                    className="tw-fixed tw-inset-0 tw-z-[999] tw-flex tw-items-center tw-justify-center tw-bg-black/90 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300"
                    onClick={closeModal}
                    style={{ zIndex: 999999 }}
                >
                    <div
                        className="tw-relative tw-h-[90vh] tw-w-[90vw] tw-max-w-7xl tw-transform tw-rounded-lg tw-bg-gray-900 tw-shadow-xl tw-overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            onClick={closeModal}
                            className="tw-absolute tw-right-4 tw-top-4 tw-z-20 tw-rounded-full tw-bg-black/60 tw-p-2 tw-text-white hover:tw-bg-black/80 tw-transition-colors tw-shadow-lg"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>

                        {/* Zoom percentage indicator */}
                        <div className="tw-absolute tw-top-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-black/60 tw-px-3 tw-py-1 tw-rounded-full tw-text-white tw-text-sm tw-font-medium tw-z-20">
                            {Math.round(scale * 100)}%
                        </div>

                        {/* Keyboard hints */}
                        <div className="tw-absolute tw-top-4 tw-left-4 tw-rounded-md tw-bg-black/60 tw-px-3 tw-py-1 tw-text-xs tw-text-white tw-z-20 tw-hidden sm:tw-block">
                            ← → Navigate | Scroll to zoom | +/- Keys | 0 Reset
                        </div>

                        {/* Zoomable Image Container */}
                        <div
                            className="tw-h-full tw-w-full tw-flex tw-justify-center tw-items-center tw-overflow-hidden"
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{ cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
                        >
                            <div
                                className="tw-h-full tw-w-full tw-transition-transform tw-duration-100 tw-ease-out tw-flex tw-justify-center tw-items-center"
                                style={{
                                    transform: `scale(${scale}) translate(${-position.x}px, ${-position.y}px)`,
                                    transformOrigin: "center",
                                }}
                            >
                                <img
                                    src={imagesArr[selectedImageIndex]?.base64 || imagesArr[selectedImageIndex]?.preview}
                                    alt={`Image ${selectedImageIndex + 1}`}
                                    className="tw-max-h-full tw-max-w-full tw-object-contain"
                                    draggable="false"
                                />
                            </div>
                        </div>

                        {/* Previous Button */}
                        {selectedImageIndex > 0 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToPrevImage();
                                }}
                                className="tw-absolute tw-left-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/60 hover:tw-bg-black/80 tw-text-white tw-rounded-full tw-p-3 tw-transition-all tw-shadow-lg tw-z-20"
                                aria-label="Previous image"
                            >
                                <FaChevronLeft className="tw-text-xl" />
                            </button>
                        )}

                        {/* Next Button */}
                        {selectedImageIndex < imagesArr.length - 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToNextImage();
                                }}
                                className="tw-absolute tw-right-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/60 hover:tw-bg-black/80 tw-text-white tw-rounded-full tw-p-3 tw-transition-all tw-shadow-lg tw-z-20"
                                aria-label="Next image"
                            >
                                <FaChevronRight className="tw-text-xl" />
                            </button>
                        )}

                        {/* Image Counter */}
                        <div className="tw-absolute tw-bottom-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-black/60 tw-text-white tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-backdrop-blur-sm tw-z-20">
                            {selectedImageIndex + 1} / {imagesArr.length}
                        </div>

                        {/* Zoom Controls */}
                        <div className="tw-absolute tw-bottom-4 tw-right-4 tw-flex tw-space-x-2 tw-z-20">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    zoomOut();
                                }}
                                className="tw-bg-black/60 tw-text-white tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-shadow-lg"
                                aria-label="Zoom out"
                            >
                                <ZoomOut size={18} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resetZoom();
                                }}
                                className="tw-bg-black/60 tw-text-white tw-px-3 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-shadow-lg tw-text-sm"
                                aria-label="Reset zoom"
                            >
                                <RotateCcw size={16} className="tw-mr-1" />
                                Reset
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    zoomIn();
                                }}
                                className="tw-bg-black/60 tw-text-white tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-shadow-lg"
                                aria-label="Zoom in"
                            >
                                <ZoomIn size={18} />
                            </button>
                        </div>

                        {/* Thumbnail Strip */}
                        {imagesArr.length > 1 && (
                            <div className="tw-absolute tw-bottom-16 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-flex tw-space-x-2 tw-z-20 tw-max-w-[80%] tw-overflow-x-auto tw-p-2 tw-bg-black/40 tw-rounded-lg">
                                {imagesArr.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedImageIndex(index);
                                            resetZoom();
                                        }}
                                        className={`tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded tw-overflow-hidden tw-border-2 tw-transition-all ${
                                            index === selectedImageIndex
                                                ? "tw-border-primary tw-ring-2 tw-ring-primary"
                                                : "tw-border-transparent hover:tw-border-white/50"
                                        }`}
                                    >
                                        <img
                                            src={img?.base64 || img?.preview}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SellImages;
